import { useCallback, useEffect, useState } from "react";
import { findPublishedCareersByCompany } from "../api/action/career.jsx";
import { academyLogger } from "utils/logger";

export const EMPLOYMENT_LABELS = {
    FULLTIME: "Full-Time",
    PARTTIME: "Part-Time",
    INTERNSHIP: "Internship",
    CONTRACT: "Contract",
    TEMPORARY: "Temporary",
};

export const WORK_MODE_LABELS = {
    REMOTE: "Remote",
    HYBRID: "Hybrid",
    ONSITE: "Onsite",
};

const asTime = (value, fallback) => {
    if (!value) {
        return fallback;
    }
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? fallback : time;
};

const sortCareers = (careers = []) => [...careers].sort((left, right) => {
    const leftOrder = Number.isFinite(Number(left?.displayOrder)) ? Number(left.displayOrder) : 9999;
    const rightOrder = Number.isFinite(Number(right?.displayOrder)) ? Number(right.displayOrder) : 9999;
    if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
    }

    const leftClose = asTime(left?.closingDate, Number.MAX_SAFE_INTEGER);
    const rightClose = asTime(right?.closingDate, Number.MAX_SAFE_INTEGER);
    if (leftClose !== rightClose) {
        return leftClose - rightClose;
    }

    return (left?.title || "").localeCompare(right?.title || "");
});

export const getEmploymentLabel = (value) => EMPLOYMENT_LABELS[value] || value || "Flexible";

export const getWorkModeLabel = (value) => WORK_MODE_LABELS[value] || value || "Flexible";

export const formatCareerDate = (value) => {
    if (!value) {
        return "Open";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "Open";
    }

    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export const getCareerDescription = (career = {}) => career.shortDescription || career.description || career.fullDescription || "";

export const careerApplyHref = (career = {}) => {
    if (career.applicationUrl) {
        return career.applicationUrl;
    }

    return "/careers#career-application";
};

export const useCareerOpenings = (companyId, options = {}) => {
    const { enabled = true } = options;
    const [careers, setCareers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const reload = useCallback(async () => {
        if (!enabled || !companyId) {
            setCareers([]);
            setLoading(false);
            return [];
        }

        setLoading(true);
        setError("");

        try {
            const items = await findPublishedCareersByCompany(companyId);
            const sorted = sortCareers(items);
            setCareers(sorted);
            return sorted;
        } catch (err) {
            academyLogger.error("Unable to load career openings", err);
            setError(err?.message || "Unable to load career openings");
            setCareers([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [companyId, enabled]);

    useEffect(() => {
        reload();
    }, [reload]);

    return {
        careers,
        loading,
        error,
        reload,
    };
};
